import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AddIcon,
    EditIcon,
    DeleteIcon,
    ScheduleIcon,
    CheckCircleIcon,
    PlayArrowIcon
} from '../../icons';
import type { ITraining, BaseVideoFormData } from '../../../types';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    defaultDropAnimationSideEffects
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { VideoUploadDialog } from '../../shared/VideoDialog';
import VideoPreviewDialog from '../../shared/VideoPreviewDialog';
import styles from './DailyTrainings.module.scss';
import { useLanguage } from '../../../i18n/LanguageContext';
import Loader from '../../shared/Loader';
import { useNotification } from '../../shared/NotificationSystem';
import ConfirmDialog from '../../shared/ConfirmDialog';
import { useTrainings } from '../../../redux/hooks';
import { isTrainingPublished, extractTimeFromPublishAt, extractDateFromPublishAt } from '../../../utils/dateUtils';
import { useVideoProcessingStatus } from '../../../hooks/useVideoProcessingStatus';

interface DailyTrainingsProps {
    onCountChange: (count: number) => void;
}

// Форматирование времени для отображения
const formatTimeDisplay = (time: string) => {
    return extractTimeFromPublishAt(time);
};

// Форматирование даты для отображения
const formatDateDisplay = (dateString: string) => {
    const dateStr = extractDateFromPublishAt(dateString);
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
};

const SortableTrainingItem = ({ training, index, onEdit, onDelete, onVideoClick, isMobile, getPreviewImage, onStatusUpdate, isNewTraining, onTrainingReady }: {
    training: ITraining;
    index: number;
    onEdit: (training: ITraining) => void;
    onDelete: (id: number) => void;
    onVideoClick: (training: ITraining) => void;
    isMobile: boolean;
    getPreviewImage: (training: ITraining) => string;
    onStatusUpdate?: (trainingId: number, updates: Partial<ITraining>) => void;
    isNewTraining?: boolean;
    onTrainingReady?: (trainingId: number) => void;
}) => {
    const { t } = useTranslation();
    const isScheduled = !isTrainingPublished(training.publish_at);
    const isDragDisabled = isMobile || !isScheduled;

    // Хук для отслеживания статуса обработки видео - только для запланированных тренировок
    useVideoProcessingStatus({
        videoId: training.id,
        autoRefresh: isScheduled,  // Автообновление только для запланированных
        refreshInterval: 3000,
        onStatusChange: (status, data) => {
            // Обновляем тренировку в Redux при получении новых данных
            if (onStatusUpdate && data) {
                const updates: Partial<ITraining> = {};

                // Обновляем video_url если получили
                if (data.url && data.url !== (typeof training.video_url === 'string' ? training.video_url : '')) {
                    (updates as any).video_url = data.url;
                }

                // Обновляем preview_url если получили
                if (data.preview_url && data.preview_url !== (typeof training.preview_url === 'string' ? training.preview_url : '')) {
                    (updates as any).preview_url = data.preview_url;
                }

                // Обновляем title если получили
                if (data.title && data.title !== training.title) {
                    updates.title = data.title;
                }

                // Если есть обновления, применяем их
                if (Object.keys(updates).length > 0) {
                    onStatusUpdate(training.id, updates);

                    // Если получили video_url или preview_url, убираем тренировку из списка новых
                    if ((updates.video_url || updates.preview_url) && onTrainingReady) {
                        onTrainingReady(training.id);
                    }
                }
            }
        }
    });

    // Показываем специальный лоадер для новых тренировок пока не готово видео или превью
    const showNewTrainingLoader = isNewTraining && (!training.video_url && !training.preview_url);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: training.id.toString(),
        disabled: isDragDisabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`${styles.draggableItem} ${isDragging ? styles.isDragging : ''} ${isScheduled ? styles.draggable : ''}`}
            {...(!isDragDisabled ? { ...attributes, ...listeners } : {})}
        >
            <div className={styles.card}>
                <div className={styles.cardActions}>
                    {isScheduled && (
                        <button
                            className={styles.iconButton}
                            title={t('dailyTrainings.editTooltip')}
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(training);
                            }}
                        >
                            <EditIcon />
                        </button>
                    )}
                    <button
                        className={styles.iconButton}
                        title={t('dailyTrainings.deleteTooltip')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(training.id);
                        }}
                    >
                        <DeleteIcon />
                    </button>
                </div>

                <div className={styles.chipContainer}>
                    <div className={`${styles.chip} ${!isScheduled ? styles.chipSuccess : styles.chipInfo}`}>
                        <span className={styles.chipIcon}>
                            {!isScheduled ? <CheckCircleIcon /> : <ScheduleIcon />}
                        </span>
                        {!isScheduled ? t('dailyTrainings.published') : t('dailyTrainings.scheduled')}
                    </div>
                </div>

                <div className={styles.cardMedia} onClick={() => onVideoClick(training)}>
                    <img
                        className={styles.cardMediaImg}
                        src={getPreviewImage(training)}
                        alt={training.title}
                    />
                    <div className={styles.playIcon}>
                        <PlayArrowIcon />
                    </div>


                    {/* Полноэкранный лоадер для новых тренировок */}
                    {showNewTrainingLoader && (
                        <div className={styles.newTrainingLoader}>
                            <div className={styles.newTrainingLoaderContent}>
                                <div className={styles.newTrainingSpinner}></div>
                                <p className={styles.newTrainingText}>
                                    {t('dailyTrainings.processing')}
                                </p>
                                <p className={styles.newTrainingSubtext}>
                                    {t('dailyTrainings.processingSubtext')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.cardContent}>
                    <h3 className={styles.cardTitle}>{training.title}</h3>

                    {training.description && (
                        <p className={styles.cardDescription}>
                            {training.description}
                        </p>
                    )}

                    {/* Отображение времени публикации (для обоих типов тренировок) */}
                    {training.publish_at && (
                        <div className={styles.timeInfo}>
                            <span className={styles.timeLabel}>
                                {t('dailyTrainings.publishTime')}: {' '}
                            </span>
                            <span className={styles.timeValue}>
                                {formatTimeDisplay(training.publish_at)}
                            </span>
                        </div>
                    )}

                    {/* Отображение даты публикации для всех тренировок */}
                    {training.publish_at && (
                        <div className={styles.dateInfo}>
                            <span className={styles.dateLabel}>
                                {t('dailyTrainings.publishDate')}: {' '}
                            </span>
                            <span className={styles.dateValue}>
                                {formatDateDisplay(training.publish_at)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DailyTrainings: React.FC<DailyTrainingsProps> = ({ onCountChange }) => {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const {
        publishedTrainings,
        scheduledTrainings,
        allPublishedTrainings,
        allScheduledTrainings,
        isLoading,
        isSaving,
        globalPublishTime,
        loadTrainings,
        addTraining,
        editTraining,
        deleteTraining,
        reorderTrainings,
        saveTrainingsOrder,
        updateTrainingFromStatus
    } = useTrainings();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewTraining, setPreviewTraining] = useState<ITraining | null>(null);
    const [activeTraining, setActiveTraining] = useState<ITraining | null>(null);
    const [activeContainerId, setActiveContainerId] = useState<string | null>(null);
    const [hasOrderChanged, setHasOrderChanged] = useState(false);
    const [initialFormData, setInitialFormData] = useState<{
        id?: number;
        title: string;
        description: string;
        videoType: 'youtube' | 'file';
        youtubeUrl?: string;
        existingVideoUrl?: string;
        existingThumbnailUrl?: string;
        publishTime?: string;
        publishDate?: string;
        isScheduled?: boolean;
        generate_preview_from_video?: boolean;
    } | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [trainingToDelete, setTrainingToDelete] = useState<ITraining | null>(null);
    const [newTrainingIds, setNewTrainingIds] = useState<Set<number>>(new Set());
    const currentLanguage = language.toLowerCase() as 'ru' | 'it';


    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const { showSuccess, showError, showWarning } = useNotification();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 600);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        loadTrainings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLanguage]);

    // Удаляем эффект, который брал время из первой запланированной тренировки
    useEffect(() => {
        // Обновляем счетчик тренировок
        const totalCount = allPublishedTrainings.length + allScheduledTrainings.length;
        onCountChange(totalCount);
    }, [allPublishedTrainings, allScheduledTrainings, onCountChange]);

    const getPreviewImage = (training: ITraining) => {
        if (typeof training.preview_url === 'string') {
            return training.preview_url;
        }
        return '';
    };

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const id = active.id.toString();

        setActiveContainerId(active.data.current?.sortable?.containerId || null);

        const training =
            publishedTrainings.find(t => t.id.toString() === id) ||
            scheduledTrainings.find(t => t.id.toString() === id) ||
            null;

        setActiveTraining(training);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        setActiveTraining(null);
        setActiveContainerId(null);

        if (!over) return;

        const activeId = active.id.toString();
        const overId = over.id.toString();

        if (activeId === overId) return;

        // Проверяем, что перетаскивание происходит только в контейнере запланированных тренировок
        const isActiveFromScheduled = active.data.current?.sortable?.containerId === 'scheduled';
        const isOverInScheduled = over.data.current?.sortable?.containerId === 'scheduled';

        // Разрешаем перетаскивание только в пределах запланированных тренировок
        if (isActiveFromScheduled && isOverInScheduled) {
            reorderTrainings(activeId, overId);
            setHasOrderChanged(true); // Отмечаем, что порядок изменился
        }
    };

    const handleAddClick = () => {
        setInitialFormData({
            title: '',
            description: '',
            videoType: 'file',
            publishTime: globalPublishTime || '10:00',
            isScheduled: true
        });
        setIsUploadDialogOpen(true);
    };

    const handleEditClick = (training: ITraining) => {
        const isScheduled = !isTrainingPublished(training.publish_at);

        if (!isScheduled) {
            showWarning('dailyTrainings.errors.cannotEditPublished');
            return;
        }

        let publishTime = globalPublishTime || '10:00';
        let publishDate = new Date().toISOString().split('T')[0];

        if (training.publish_at) {
            publishTime = extractTimeFromPublishAt(training.publish_at);
            publishDate = extractDateFromPublishAt(training.publish_at);
        }

        setIsUploadDialogOpen(true);
        setInitialFormData({
            id: training.id,
            title: training.title,
            description: training.description || 'Описание тренировки',
            videoType: training.video_type === 'youtube' ? 'youtube' : 'file',
            youtubeUrl: training.youtube_url,
            existingVideoUrl: typeof training.video_url === 'string' ? training.video_url : '',
            existingThumbnailUrl: training.preview_url ? (typeof training.preview_url === 'string' ? training.preview_url : '') : '',
            publishTime: publishTime,
            publishDate: publishDate,
            isScheduled: isScheduled,
            generate_preview_from_video: training.generate_preview_from_video
        });
    };

    const handleDeleteClick = async (id: number) => {
        const training = [...publishedTrainings, ...scheduledTrainings].find(t => t.id === id);

        if (!training) {
            showError('dailyTrainings.errors.trainingNotFound');
            return;
        }

        // Проверяем, совпадает ли язык текущего администратора с языком тренировки
        if (training.language && training.language.toUpperCase() !== currentLanguage.toUpperCase()) {
            showWarning('dailyTrainings.errors.wrongLanguage', {
                trainingLanguage: training.language,
                currentLanguage: currentLanguage.toUpperCase()
            });
            return;
        }

        setTrainingToDelete(training);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!trainingToDelete) return;

        try {
            await deleteTraining(trainingToDelete.id);
            showSuccess('dailyTrainings.success.deleted');
        } catch (err) {
            console.error('Ошибка при удалении тренировки:', err);
            showError('dailyTrainings.errors.deleteFailed');
        } finally {
            setDeleteDialogOpen(false);
            setTrainingToDelete(null);
        }
    };

    const handleVideoSubmit = async (data: BaseVideoFormData) => {
        try {
            if (initialFormData?.id) {
                // При редактировании - просто отправляем данные
                await editTraining(initialFormData.id, data);
            } else {
                // При создании - проверяем наличие видео или YouTube URL
                // Проверяем наличие видеофайла или ютуб-урл только при создании новых тренировок
                const hasVideoFile = data.videoFile !== undefined && data.videoFile !== null;
                const hasYoutubeUrl = data.training.youtube_url !== undefined && data.training.youtube_url.trim() !== '';

                if (!hasVideoFile && !hasYoutubeUrl) {
                    showError('dailyTrainings.errors.videoRequired');
                    return;
                }

                try {
                    const result = await addTraining(data);

                    // Если добавление прошло успешно и мы получили ID новой тренировки
                    if (result && typeof result === 'object' && 'type' in result && result.type.endsWith('/fulfilled') && 'payload' in result && result.payload && typeof result.payload === 'object' && 'id' in result.payload) {
                        const trainingId = (result.payload as any).id;
                        setNewTrainingIds(prev => new Set(prev).add(trainingId));

                        // Убираем ID из списка новых через некоторое время (когда видео будет готово)
                        setTimeout(() => {
                            setNewTrainingIds(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(trainingId);
                                return newSet;
                            });
                        }, 30000); // 30 секунд на обработку
                    }
                } catch (addError) {
                    console.error('Ошибка при добавлении тренировки:', addError);
                    throw addError;
                }
            }

            setIsUploadDialogOpen(false);
        } catch (err) {
            console.error('Ошибка при отправке тренировки:', err);
            showError('dailyTrainings.errors.uploadFailed');
        }
    };

    const handleSaveAll = async () => {
        try {
            const res = await saveTrainingsOrder();
            if (res.type.endsWith('/fulfilled')) {
                showSuccess('dailyTrainings.success.saved');
                setHasOrderChanged(false); // Сбрасываем флаг изменений порядка
            } else {
                showError('dailyTrainings.errors.saveFailed');
            }
        } catch (error) {
            showError('dailyTrainings.errors.saveFailed');
        }
    };

    const handleVideoClick = (training: ITraining) => {
        setPreviewTraining(training);
        setIsPreviewOpen(true);
    };

    const handleTrainingReady = (trainingId: number) => {
        setNewTrainingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(trainingId);
            return newSet;
        });
    };

    const renderTrainingList = (listType: 'published' | 'scheduled') => {
        let trainings = listType === 'published' ? publishedTrainings : scheduledTrainings;

        const maxTrainings = currentLanguage === 'ru' ? 12 : 10;

        if (listType === 'published') {
            // Сортируем опубликованные по дате публикации по убыванию (самые новые сначала)
            trainings = [...trainings].sort((a, b) => {
                const dateA = a.publish_at ? new Date(a.publish_at).getTime() : 0;
                const dateB = b.publish_at ? new Date(b.publish_at).getTime() : 0;
                return dateB - dateA;
            });
        } else {
            // Сортируем запланированные по order (порядку) по возрастанию
            trainings = [...trainings].sort((a, b) => {
                const orderA = a.order || 0;
                const orderB = b.order || 0;
                return orderA - orderB;
            });
        }

        // Ограничиваем количество отображаемых тренировок
        trainings = trainings.slice(0, maxTrainings);

        let titleWithCount = '';

        if (listType === 'published') {
            const count = `${trainings.length}/${maxTrainings}`;
            titleWithCount = `${t('dailyTrainings.publishedTrainings')} (${count})`;
        } else {
            titleWithCount = `${t('dailyTrainings.scheduledTrainings')} (${trainings.length})`;
        }

        return (
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        {titleWithCount}
                    </h2>
                    {/* Убираем глобальные контролы времени */}
                </div>

                <SortableContext
                    id={listType}
                    items={trainings.map(t => t.id.toString())}
                    strategy={rectSortingStrategy}
                >
                    <div className={`${styles.grid} ${activeContainerId === listType ? styles.draggingOver : ''}`}>
                        {trainings.map((training, index) => (
                            <SortableTrainingItem
                                key={training.id.toString()}
                                training={training}
                                index={index}
                                onEdit={handleEditClick}
                                onDelete={handleDeleteClick}
                                onVideoClick={handleVideoClick}
                                isMobile={isMobile}
                                getPreviewImage={getPreviewImage}
                                onStatusUpdate={updateTrainingFromStatus}
                                isNewTraining={newTrainingIds.has(training.id)}
                                onTrainingReady={handleTrainingReady}
                            />
                        ))}
                        {trainings.length === 0 && (
                            <div className={styles.emptyState}>
                                <p className={styles.emptyText}>
                                    {t('dailyTrainings.noTrainings')}
                                </p>
                            </div>
                        )}
                    </div>
                </SortableContext>
            </section>
        );
    };

    if (isLoading && publishedTrainings.length === 0 && scheduledTrainings.length === 0) {
        return (
            <div className={styles.loadingContainer}>
                <Loader />
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1 className={styles.headerTitle}>
                    {t('dailyTrainings.pageTitle')}
                </h1>
                <div className={styles.buttonGroup}>
                    <button
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        onClick={handleAddClick}
                    >
                        <AddIcon /> {t('dailyTrainings.addButton')}
                    </button>
                    <button
                        className={`${styles.button} ${styles.buttonSuccess} ${isSaving ? styles.buttonDisabled : ''} ${!hasOrderChanged ? styles.buttonDisabled : ''}`}
                        onClick={handleSaveAll}
                        disabled={isSaving || !hasOrderChanged}
                    >
                        {isSaving ? (
                            <span className={styles.spinnerContainer}>
                                <span className={styles.spinner}></span>
                            </span>
                        ) : t('dailyTrainings.saveButton')}
                    </button>
                </div>
            </header>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                {renderTrainingList('published')}
                {renderTrainingList('scheduled')}

                <DragOverlay dropAnimation={{
                    sideEffects: defaultDropAnimationSideEffects({
                        styles: {
                            active: {
                                opacity: '0.5'
                            }
                        }
                    })
                }}>
                    {activeTraining && (
                        <div className={`${styles.draggableItem} ${styles.isDragging}`}>
                            <div className={styles.card}>
                                <div className={styles.cardActions}>
                                    {activeTraining.publish_at && isTrainingPublished(activeTraining.publish_at) === false && (
                                        <>
                                            <button className={styles.iconButton} title={t('dailyTrainings.editTooltip')}>
                                                <EditIcon />
                                            </button>
                                            <button className={styles.iconButton} title={t('dailyTrainings.deleteTooltip')}>
                                                <DeleteIcon />
                                            </button>
                                        </>
                                    )}
                                </div>

                                <div className={styles.chipContainer}>
                                    <div className={`${styles.chip} ${styles.chipInfo}`}>
                                        <span className={styles.chipIcon}>
                                            <ScheduleIcon />
                                        </span>
                                        {t('dailyTrainings.scheduled')}
                                    </div>
                                </div>

                                <div className={styles.cardMedia}>
                                    <img
                                        className={styles.cardMediaImg}
                                        src={getPreviewImage(activeTraining)}
                                        alt={activeTraining.title}
                                    />
                                    <div className={styles.playIcon}>
                                        <PlayArrowIcon />
                                    </div>
                                    {/* В DragOverlay не показываем индикатор состояния, так как это только для перетаскивания */}
                                </div>

                                <div className={styles.cardContent}>
                                    <h3 className={styles.cardTitle}>{activeTraining.title}</h3>

                                    {activeTraining.description && (
                                        <p className={styles.cardDescription}>
                                            {activeTraining.description}
                                        </p>
                                    )}

                                    {activeTraining.publish_at && (
                                        <div className={styles.timeInfo}>
                                            <span className={styles.timeLabel}>
                                                {t('dailyTrainings.publishTime')}: {' '}
                                            </span>
                                            <span className={styles.timeValue}>
                                                {formatTimeDisplay(activeTraining.publish_at)}
                                            </span>
                                        </div>
                                    )}

                                    {activeTraining.publish_at && (
                                        <div className={styles.dateInfo}>
                                            <span className={styles.dateLabel}>
                                                {t('dailyTrainings.publishDate')}: {' '}
                                            </span>
                                            <span className={styles.dateValue}>
                                                {formatDateDisplay(activeTraining.publish_at)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </DragOverlay>
            </DndContext>

            <VideoPreviewDialog
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                title={previewTraining?.title || ''}
                videoUrl={(previewTraining?.video_url && typeof previewTraining.video_url === 'string' ?
                    previewTraining.video_url : previewTraining?.youtube_url) || ''}
                isYouTube={previewTraining?.video_type === 'youtube'}
            />

            <VideoUploadDialog
                open={isUploadDialogOpen}
                onClose={() => setIsUploadDialogOpen(false)}
                onSubmit={handleVideoSubmit}
                dialogTitle={t('dailyTrainings.uploadDialog.title')}
                initialData={initialFormData}
                defaultPublishTime={globalPublishTime}
                showPublishTime={true}
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                title={t('dailyTrainings.deleteDialog.title')}
                message={t('dailyTrainings.deleteDialog.message', { title: trainingToDelete?.title })}
                onConfirm={handleConfirmDelete}
                onCancel={() => {
                    setDeleteDialogOpen(false);
                    setTrainingToDelete(null);
                }}
            />
        </div>
    );
};

export default DailyTrainings;